import React from 'react';
import { Link } from 'react-router-dom';

interface HelpfulToolsProps {
    project: any;
    addToCompare?: (project: any) => void;
    compareList?: any[];
    scrollToSection?: (sectionId: string) => void;
}

const HelpfulTools: React.FC<HelpfulToolsProps> = ({ project, addToCompare, compareList = [], scrollToSection }) => {
    const isAdded = compareList.some(p => p._id === project._id);

    const tools = [
        {
            title: 'EMI',
            subtitle: 'Calculator',
            desc: 'Check monthly installments for your dream home.',
            imageUrl: '/assets/help-full-tools/emi-calc.png',
            link: '/emi-calculator'
        },
        {
            title: 'Affordability',
            subtitle: 'Calculator',
            desc: 'Find the best budget for your home search based on your income.',
            imageUrl: '/assets/help-full-tools/affordability.png',
            link: '/affordability-calculator'
        },
        {
            title: 'Eligibility',
            subtitle: 'Calculator',
            desc: 'Check your home loan eligibility in just a few steps.',
            imageUrl: '/assets/help-full-tools/eligibility.png',
            link: '/eligibility-calculator'
        },
        {
            title: 'VASTU',
            subtitle: 'Calculator',
            desc: 'Check Vastu compliance for your home layout.',
            imageUrl: '/assets/help-full-tools/ai-vastu.png',
            link: '/vastu-calculator-process'
        },
        {
            title: 'Real-time',
            subtitle: 'Trends',
            desc: 'Check latest property rates & market trends for this location.',
            imageUrl: '/assets/help-full-tools/rental-yield.png',
            link: `/location/${project.location?.toLowerCase().replace(/\s+/g, '-') || 'vaishno-devi-circle'}`
        },
        {
            title: isAdded ? 'Added to' : 'Add to',
            subtitle: 'Compare',
            desc: isAdded ? 'Project added to your comparison list.' : 'Compare this project with others to find your best fit.',
            imageUrl: '/assets/help-full-tools/investment.png',
            onClick: () => addToCompare && addToCompare(project),
            isAction: true,
            active: isAdded
        }
    ];

    return (
        <div id="helpful-tools" className="card border-0 shadow-2xl rounded-[2.5rem] overflow-hidden bg-white ring-1 ring-gray-100 shadow-blue-900/5 transition-all hover:shadow-blue-900/10" style={{ borderLeft: '8px solid #f1ae24' }}>
            <div className="px-5 md:px-8 py-5 md:py-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                    <h5 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] mb-0 italic">Helpful Tools</h5>
                    <p className="text-[#05144b]/60 text-[11px] md:text-[12px] font-medium leading-relaxed opacity-60 mb-0">Check affordability, eligibility & EMI</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#05144b]/5 flex items-center justify-center text-[#f1ae24]">
                    <i className="fa-solid fa-screwdriver-wrench text-lg"></i>
                </div>
            </div>

            <div className="p-4 md:p-5 bg-gray-50/5">
                <div className="grid grid-cols-1 gap-3 md:gap-4">
                    {tools.map((tool, idx) => {
                        const Content = (
                            <div className={`bg-white rounded-xl border ${tool.active ? 'border-[#f1ae24] bg-[#f1ae24]/5' : 'border-gray-100'} p-4 flex items-center gap-4 hover:shadow-lg transition-all cursor-pointer group hover:border-[#f1ae24]/30 h-full w-full`}>
                                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${tool.active ? 'bg-[#f1ae24]/10' : 'bg-slate-50'} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300 shadow-sm`}>
                                    <img 
                                        src={tool.imageUrl} 
                                        className="w-6 h-6 md:w-7 md:h-7 object-contain" 
                                        alt={tool.title} 
                                    />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                    <h6 className="text-[#05144b] font-black text-base md:text-[17px] mb-0.5 leading-tight group-hover:text-[#4c1d95] transition-colors">
                                        {tool.title} <span className="text-[#05144b]/70 font-bold">{tool.subtitle}</span>
                                        {tool.active && <i className="fa-solid fa-circle-check text-[#f1ae24] ml-2 text-xs"></i>}
                                    </h6>
                                    <p className="text-[#05144b] text-xs md:text-[13px] leading-tight font-medium line-clamp-1 opacity-70">
                                        {tool.desc}
                                    </p>
                                </div>
                            </div>
                        );

                        if (tool.isAction) {
                            return (
                                <button key={idx} onClick={tool.onClick} className="p-0 border-0 bg-transparent w-full text-left block no-underline outline-none">
                                    {Content}
                                </button>
                            );
                        }

                        return (
                            <Link to={tool.link || '#'} key={idx} className="block no-underline">
                                {Content}
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default HelpfulTools;
